import { useEffect, useRef, useState } from "react";
import { db } from "../config/firebase";
import {
  collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getCustomerRecords, invalidateCustomerCache } from "../services/dataCache";

// Store-based linking (2026-08-20, Brian's request): the link now lives on
// the ACCOUNT (users.customerRecordId → a customers record), not on the
// record. Several accounts — e.g. two managers of one store — may point at
// the same record; all of them invoice under the record's exact QB name and
// see all of the store's orders. The old customers.linkedUserId field is
// migrated on first admin visit and then ignored (left in place, harmless).
export default function AdminCustomers() {
  const { user, profile } = useAuth();
  // Reps get a READ-ONLY view (settled 2026-07-10): they see records and
  // linked accounts but cannot author QB names — adding a record or editing
  // a QB name can break invoice sync (QB error 3140), so that stays admin-only.
  // firestore.rules enforces the same server-side.
  const readOnly = profile?.role !== "admin";
  const [records, setRecords] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migratedMsg, setMigratedMsg] = useState("");
  const migrationRan = useRef(false);

  // Add-record form
  const [newName, setNewName] = useState("");
  const [newQbName, setNewQbName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  const [recordSearch, setRecordSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [recordEdits, setRecordEdits] = useState({}); // recordId -> qbName being edited
  const [accountEdits, setAccountEdits] = useState({}); // uid -> qbName being edited
  const [savedFlash, setSavedFlash] = useState(null);

  // Per-store special pricing counts for the 💲 badge — editing happens on
  // the dedicated /admin/pricing page (Brian, 2026-08-24).
  const [allPricing, setAllPricing] = useState({}); // recordId -> doc data

  useEffect(() => {
    async function load() {
      const [cachedRecs, accSnap] = await Promise.all([
        getCustomerRecords(), // session cache — 0 reads on revisit
        getDocs(query(collection(db, "users"), where("role", "==", "customer")))
      ]);
      const recs = [...cachedRecs].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRecords(recs);
      setAccounts(accSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
      if (profile?.role === "admin") {
        try {
          const priceSnap = await getDocs(collection(db, "customerPricing"));
          setAllPricing(Object.fromEntries(priceSnap.docs.map(d => [d.id, d.data()])));
        } catch (err) {
          console.error("Could not load special pricing counts:", err.message);
        }
      }
      setLoading(false);
    }
    load();
  }, [profile?.role]);

  // One-time migration: any record still holding the OLD linkedUserId whose
  // account lacks the NEW customerRecordId gets converted (write goes on the
  // user doc). Admin only — user-doc writes are admin-only in the rules.
  useEffect(() => {
    if (loading || readOnly || migrationRan.current) return;
    migrationRan.current = true;
    async function migrate() {
      let count = 0;
      const updated = [];
      for (const r of records) {
        if (!r.linkedUserId) continue;
        const acc = accounts.find(a => a.uid === r.linkedUserId);
        if (!acc || acc.customerRecordId) continue;
        try {
          await updateDoc(doc(db, "users", acc.uid), {
            customerRecordId: r.id,
            qbCustomerName: r.qbCustomerName
          });
          updated.push({ uid: acc.uid, customerRecordId: r.id, qbCustomerName: r.qbCustomerName });
          count++;
        } catch (err) {
          console.error(`Migration failed for account ${acc.uid}: ${err.message}`);
        }
      }
      if (count > 0) {
        setAccounts(prev => prev.map(a => {
          const u = updated.find(x => x.uid === a.uid);
          return u ? { ...a, customerRecordId: u.customerRecordId, qbCustomerName: u.qbCustomerName } : a;
        }));
        setMigratedMsg(`Migrated ${count} account link${count === 1 ? "" : "s"} to the new store-based format.`);
      }
    }
    migrate();
  }, [loading, readOnly, records, accounts]);

  function flash(key) {
    setSavedFlash(key);
    setTimeout(() => setSavedFlash(null), 2000);
  }

  function formatDate(ts) {
    if (!ts || !ts.toDate) return "—";
    return ts.toDate().toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
  }

  // Accounts linked to a record: the new field, plus the legacy pointer for
  // records not yet migrated (covers the rep view, where migration can't run).
  function linkedAccountsFor(r) {
    return accounts.filter(a =>
      a.customerRecordId === r.id || (!a.customerRecordId && r.linkedUserId === a.uid)
    );
  }

  async function handleAddRecord(e) {
    e.preventDefault();
    setAddError("");
    const name = newName.trim();
    const qbName = newQbName.trim();
    if (!name || !qbName) {
      setAddError("Name and QuickBooks name are both required.");
      return;
    }
    const dupe = records.find(
      r => r.qbCustomerName.toLowerCase() === qbName.toLowerCase()
    );
    if (dupe) {
      setAddError(`A record with that QuickBooks name already exists ("${dupe.name}"). No duplicates allowed.`);
      return;
    }
    setAdding(true);
    try {
      const data = {
        name,
        qbCustomerName: qbName,
        phone: newPhone.trim() || null,
        linkedUserId: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid
      };
      const ref = await addDoc(collection(db, "customers"), data);
      invalidateCustomerCache();
      setRecords(prev =>
        [...prev, { id: ref.id, ...data }].sort((a, b) => a.name.localeCompare(b.name))
      );
      setNewName("");
      setNewQbName("");
      setNewPhone("");
    } catch (err) {
      setAddError(`Could not add customer: ${err.message}`);
    } finally {
      setAdding(false);
    }
  }

  async function saveRecordQbName(recordId) {
    const value = (recordEdits[recordId] ?? "").trim();
    if (!value) return; // QB name on a record must never be blank
    await updateDoc(doc(db, "customers", recordId), { qbCustomerName: value });
    invalidateCustomerCache();
    setRecords(prev => prev.map(r => r.id === recordId ? { ...r, qbCustomerName: value } : r));
    // Keep the copied QB name on every linked account in step (fallback path
    // for old orders that sync by account instead of record).
    const linkedUids = accounts.filter(a => a.customerRecordId === recordId).map(a => a.uid);
    for (const uid of linkedUids) {
      try {
        await updateDoc(doc(db, "users", uid), { qbCustomerName: value });
      } catch (err) {
        console.error(`Could not update QB name on account ${uid}: ${err.message}`);
      }
    }
    if (linkedUids.length > 0) {
      setAccounts(prev => prev.map(a => linkedUids.includes(a.uid) ? { ...a, qbCustomerName: value } : a));
    }
    setRecordEdits(prev => { const n = { ...prev }; delete n[recordId]; return n; });
    flash(`rec-${recordId}`);
  }

  // Assign (or clear) an account's store. Linking copies the record's exact
  // QB name onto the account so legacy-path invoices still match.
  async function assignStore(a, recordId) {
    const record = recordId ? records.find(r => r.id === recordId) : null;
    if (recordId && !record) return;
    try {
      await updateDoc(doc(db, "users", a.uid), {
        customerRecordId: recordId || null,
        ...(record ? { qbCustomerName: record.qbCustomerName } : {})
      });
      setAccounts(prev => prev.map(x => x.uid === a.uid
        ? { ...x, customerRecordId: recordId || null, ...(record ? { qbCustomerName: record.qbCustomerName } : {}) }
        : x));
      flash(`acc-${a.uid}`);
    } catch (err) {
      alert(`Could not update the store link: ${err.message}`);
    }
  }

  async function saveAccountQbName(uid) {
    const value = (accountEdits[uid] ?? "").trim();
    await updateDoc(doc(db, "users", uid), { qbCustomerName: value || null });
    setAccounts(prev => prev.map(a => a.uid === uid ? { ...a, qbCustomerName: value || null } : a));
    setAccountEdits(prev => { const n = { ...prev }; delete n[uid]; return n; });
    flash(`acc-${uid}`);
  }

  // Per-customer price visibility (backlog #4): hides ONLY prices in the
  // customer's catalog/cart/orders — they always see stock and can order.
  async function toggleShowPrices(a, checked) {
    setAccounts(prev => prev.map(x => x.uid === a.uid ? { ...x, showPrices: checked } : x));
    try {
      await updateDoc(doc(db, "users", a.uid), { showPrices: checked });
      flash(`acc-${a.uid}`);
    } catch (err) {
      setAccounts(prev => prev.map(x => x.uid === a.uid ? { ...x, showPrices: a.showPrices } : x));
      alert(`Could not save price visibility: ${err.message}`);
    }
  }

  // Revoke / restore portal access. Revoked accounts cannot log in, and the
  // Firestore rules block an already-open session from reading products or
  // placing orders. Their order history and store link are kept.
  async function setAccountStatus(a, status) {
    if (status === "revoked" && !window.confirm(
      `Revoke portal access for ${a.companyName || a.name || a.email}?\n\nThey will no longer be able to log in or place orders. You can restore access any time.`)) {
      return;
    }
    try {
      await updateDoc(doc(db, "users", a.uid), {
        status,
        statusChangedBy: user.uid,
        statusChangedAt: serverTimestamp()
      });
      setAccounts(prev => prev.map(x => x.uid === a.uid ? { ...x, status } : x));
      flash(`acc-${a.uid}`);
    } catch (err) {
      alert(`Could not change account status: ${err.message}`);
    }
  }

  function pricingCount(recordId) {
    return Object.keys(allPricing[recordId]?.items || {}).length;
  }

  const filteredRecords = records.filter(r => {
    const s = recordSearch.toLowerCase();
    return (
      r.name.toLowerCase().includes(s) ||
      r.qbCustomerName.toLowerCase().includes(s) ||
      (r.phone || "").includes(recordSearch)
    );
  });

  const sortedAccounts = [...accounts].sort((a, b) =>
    (a.companyName || a.name || "").localeCompare(b.companyName || b.name || ""));
  const filteredAccounts = sortedAccounts.filter(a => {
    const s = accountSearch.toLowerCase();
    if (!s) return true;
    return (
      (a.companyName || "").toLowerCase().includes(s) ||
      (a.name || "").toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s) ||
      (a.phone || "").includes(accountSearch)
    );
  });

  // Everything that identifies an account at a glance — the fix for two
  // registrations both called "Kim's Mart".
  function accountLabel(a) {
    const bits = [a.companyName || a.name || a.email];
    if (a.companyName && a.name) bits.push(a.name);
    if (a.phone) bits.push(a.phone);
    return bits.join(" · ");
  }

  function statusText(s) {
    if (s === "approved") return <span className="linked-badge">approved</span>;
    if (s === "revoked") return <strong style={{ color: "#c0392b" }}>revoked</strong>;
    if (s === "rejected") return <strong style={{ color: "#c0392b" }}>rejected</strong>;
    if (s === "pending") return <em>pending — see Approvals</em>;
    return s || "—";
  }

  if (loading) return <p>Loading customers...</p>;

  return (
    <div className="admin-customers">
      <h2>Customer Records ({records.length})</h2>
      <p className="inventory-help">
        These are your customers as they exist in QuickBooks — they do NOT need portal
        accounts. Reps order on their behalf from this list. The QuickBooks name
        must match QuickBooks EXACTLY or invoice sync will fail. Portal accounts are
        linked to a store in the Account Holders table below.
        {readOnly && <strong> You have view-only access — QuickBooks names are managed by the admin.</strong>}
      </p>
      {migratedMsg && <p className="inventory-help"><strong>{migratedMsg}</strong></p>}

      {!readOnly && (
        <>
          <form className="customer-add-form" onSubmit={handleAddRecord}>
            <input
              placeholder="Customer name (display)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              placeholder="Exact QuickBooks customer name"
              value={newQbName}
              onChange={(e) => setNewQbName(e.target.value)}
            />
            <input
              placeholder="Phone (optional)"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
            />
            <button type="submit" disabled={adding}>
              {adding ? "Adding..." : "+ Add Customer"}
            </button>
          </form>
          {addError && <p className="error">{addError}</p>}
        </>
      )}

      {records.length > 0 && (
        <>
          <input
            className="record-search"
            placeholder="Search records by name, QB name, or phone..."
            value={recordSearch}
            onChange={(e) => setRecordSearch(e.target.value)}
          />
          <table>
            <thead>
              <tr>
                <th>Name</th><th>QuickBooks Name</th><th>Phone</th>
                <th>Linked Accounts</th>{!readOnly && <th>Special pricing</th>}{!readOnly && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => {
                const editing = recordEdits[r.id] !== undefined;
                const inputValue = editing ? recordEdits[r.id] : r.qbCustomerName;
                const linked = linkedAccountsFor(r);
                return (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>
                      {readOnly ? (
                        r.qbCustomerName
                      ) : (
                        <input
                          className="qbname-input"
                          value={inputValue}
                          onChange={(e) => setRecordEdits(prev => ({ ...prev, [r.id]: e.target.value }))}
                        />
                      )}
                    </td>
                    <td>{r.phone || "—"}</td>
                    <td>
                      {linked.length === 0 ? "—" : linked.map(a => (
                        <div key={a.uid}>
                          <span className="linked-badge">✓ {accountLabel(a)}</span>
                        </div>
                      ))}
                    </td>
                    {!readOnly && (
                      <td>
                        <Link className="mini-btn" to={`/admin/pricing?record=${r.id}`}>
                          💲 {pricingCount(r.id) > 0 ? pricingCount(r.id) : "—"}
                        </Link>
                      </td>
                    )}
                    {!readOnly && (
                      <td>
                        <button onClick={() => saveRecordQbName(r.id)} disabled={!editing || !(recordEdits[r.id] ?? "").trim()}>
                          {savedFlash === `rec-${r.id}` ? "Saved ✓" : "Save"}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {records.length === 0 && (
        <p className="pick-empty">No customer records yet. Add one above, or bulk-import your QuickBooks customer list.</p>
      )}

      {!readOnly && (
        <>
          <h2 className="accounts-heading">Account Holders ({accounts.length})</h2>
          <p className="inventory-help">
            Everyone who registered on the portal, with their registration details. Pick a
            <strong> Store</strong> to say which customer record an account belongs to — several
            accounts (e.g. two managers) can share one store: they all invoice under the store's
            QuickBooks name and all see the store's orders. <strong>Revoke access</strong> blocks
            an account from logging in or ordering; you can restore it any time. The QB name
            field is only for accounts with no store.
          </p>
          {accounts.length === 0 ? (
            <p className="pick-empty">No customer accounts yet.</p>
          ) : (
            <>
              <input
                className="record-search"
                placeholder="Search accounts by company, contact, email, or phone..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
              />
              <table>
                <thead>
                  <tr>
                    <th>Company (portal)</th><th>Contact</th><th>Phone</th><th>Email</th>
                    <th>Registered</th><th>Status</th><th>Store</th>
                    <th>QuickBooks Customer Name</th><th>Show prices</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map(a => {
                    const editing = accountEdits[a.uid] !== undefined;
                    const inputValue = editing ? accountEdits[a.uid] : (a.qbCustomerName || "");
                    const isLinked = !!a.customerRecordId;
                    return (
                      <tr key={a.uid}>
                        <td>{a.companyName || "—"}</td>
                        <td>{a.name || "—"}</td>
                        <td>{a.phone || "—"}</td>
                        <td>{a.email || "—"}</td>
                        <td>{formatDate(a.createdAt)}</td>
                        <td>{statusText(a.status)}</td>
                        <td>
                          <select
                            className="link-select"
                            value={a.customerRecordId || ""}
                            onChange={(e) => assignStore(a, e.target.value)}
                          >
                            <option value="">— no store —</option>
                            {records.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="qbname-input"
                            placeholder="(same as portal name)"
                            value={inputValue}
                            disabled={isLinked}
                            title={isLinked ? "Linked accounts use the store record's QB name" : undefined}
                            onChange={(e) => setAccountEdits(prev => ({ ...prev, [a.uid]: e.target.value }))}
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={a.showPrices !== false}
                            onChange={(e) => toggleShowPrices(a, e.target.checked)}
                            title="Unchecked: this customer sees stock and can order, but sees NO prices"
                          />
                        </td>
                        <td>
                          <button onClick={() => saveAccountQbName(a.uid)} disabled={!editing || isLinked}>
                            {savedFlash === `acc-${a.uid}` ? "Saved ✓" : "Save"}
                          </button>
                          {a.status === "approved" && (
                            <button className="mini-btn danger" onClick={() => setAccountStatus(a, "revoked")}>
                              Revoke access
                            </button>
                          )}
                          {(a.status === "revoked" || a.status === "rejected") && (
                            <button className="mini-btn" onClick={() => setAccountStatus(a, "approved")}>
                              Restore access
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredAccounts.length === 0 && (
                <p className="pick-empty">No accounts match that search.</p>
              )}
            </>
          )}
        </>
      )}

    </div>
  );
}
